import * as React from "react";
import CloseIcon from "@mui/icons-material/Close";
import MilestoneSelect from "./MilestoneSelect";
import { DrawerState, Milestone } from "./communicationTemplate.types";

interface TemplateFormDrawerProps {
    open: boolean;
    state: DrawerState;
    milestoneOptions: Milestone[];
    onChange: (updater: (s: DrawerState) => DrawerState) => void;
    onClose: () => void;
    onSave: () => void;
    onEditFlags: () => void;
}

type Errors = {
    templateName?: string;
    waMilestones?: string;
    emailMilestones?: string;
};

export default function TemplateFormDrawer({
    open,
    state,
    milestoneOptions,
    onChange,
    onClose,
    onSave,
    onEditFlags,
}: TemplateFormDrawerProps) {
    const isEdit = !!state.id;
    const [errors, setErrors] = React.useState<Errors>({});

    // Clear errors whenever the drawer reopens
    React.useEffect(() => {
        if (open) setErrors({});
    }, [open]);

    const validate = (): boolean => {
        const next: Errors = {};
        if (!state.templateName.trim()) next.templateName = "Template name is required";
        if (!state.waMilestones.length) next.waMilestones = "At least one WhatsApp milestone is required";
        if (!state.emailMilestones.length) next.emailMilestones = "At least one email milestone is required";
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSaveClick = () => {
        if (!validate()) return;   // stop — inline errors shown
        onSave();
    };

    return (
        <>
            <div className={`ctpl-overlay${open ? " open" : ""}`} onClick={onClose} />
            <div className={`ctpl-drawer${open ? " open" : ""}`}>
                <div className="ctpl-drawer-header">
                    <h2>{isEdit ? `Edit — ${state.templateName}` : "Add Template"}</h2>
                    <button className="ctpl-icon-btn" onClick={onClose}>
                        <CloseIcon fontSize="small" />
                    </button>
                </div>

                <div className="ctpl-drawer-body">
                    <div className="ctpl-field-group">
                        <div className="ctpl-section-label">Template info</div>
                        <label className="ctpl-field-label">
                            Template name <span className="ctpl-required">*</span>
                        </label>
                        <input
                            className={`ctpl-field-input${errors.templateName ? " error" : ""}`}
                            placeholder="e.g. Booking Confirmed"
                            value={state.templateName}
                            onChange={(e) => {
                                const v = e.target.value;
                                onChange((s) => ({ ...s, templateName: v }));
                                if (errors.templateName && v.trim())
                                    setErrors((prev) => ({ ...prev, templateName: undefined }));
                            }}
                        />
                        {errors.templateName && (
                            <div className="ctpl-field-error">{errors.templateName}</div>
                        )}
                    </div>

                    <div className="ctpl-field-group">
                        <div className="ctpl-section-label">💬 WhatsApp channel</div>
                        <label className="ctpl-field-label">
                            Milestones <span className="ctpl-required">*</span>
                        </label>
                        <div className={errors.waMilestones ? "ctpl-field-invalid" : ""}>
                            <MilestoneSelect
                                className="ctpl-autocomplete ctpl-chip-field"
                                options={milestoneOptions}
                                value={state.waMilestones}
                                onChange={(v) => {
                                    onChange((s) => ({ ...s, waMilestones: v }));
                                    if (errors.waMilestones && v.length)
                                        setErrors((prev) => ({ ...prev, waMilestones: undefined }));
                                }}
                            />
                        </div>
                        {errors.waMilestones && (
                            <div className="ctpl-field-error">{errors.waMilestones}</div>
                        )}
                    </div>

                    <div className="ctpl-field-group">
                        <div className="ctpl-section-label">✉️ Email channel</div>
                        <label className="ctpl-field-label">
                            Milestones <span className="ctpl-required">*</span>
                        </label>
                        <div className={errors.emailMilestones ? "ctpl-field-invalid" : ""}>
                            <MilestoneSelect
                                className="ctpl-autocomplete ctpl-chip-field"
                                options={milestoneOptions}
                                value={state.emailMilestones}
                                onChange={(v) => {
                                    onChange((s) => ({ ...s, emailMilestones: v }));
                                    if (errors.emailMilestones && v.length)
                                        setErrors((prev) => ({ ...prev, emailMilestones: undefined }));
                                }}
                            />
                        </div>
                        {errors.emailMilestones && (
                            <div className="ctpl-field-error">{errors.emailMilestones}</div>
                        )}
                    </div>

                    <div className="ctpl-field-group">
                        <div className="ctpl-section-label">🏷 Flags <span className="ctpl-optional">(optional)</span></div>
                        <button
                            type="button"
                            className="ctpl-flags-field"
                            onClick={onEditFlags}
                        >
                            {state.flags.length ? (
                                state.flags.map((f: any, i: number) => (
                                    <span className="ctpl-chip ctpl-flag-chip-selected" key={f?._id || i}>
                                        {f?.category ? `${f.category}: ${f.name}` : f?.name}
                                        <span
                                            className="ctpl-chip-remove"
                                            role="button"
                                            tabIndex={0}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onChange((s) => ({
                                                    ...s,
                                                    flags: s.flags.filter((_, idx) => idx !== i),
                                                }));
                                            }}
                                        >
                                            ×
                                        </span>
                                    </span>
                                ))
                            ) : (
                                <span className="ctpl-chip-placeholder">Click to select flags</span>
                            )}
                        </button>
                    </div>

                    {/* <div className="ctpl-toggle-row">
                        <div>
                            <div style={{ fontSize: 13.5, fontWeight: 600 }}>Active</div>
                            <div className="ctpl-hint">Inactive templates won't be used to send messages.</div>
                        </div>
                        <button
                            type="button"
                            className={`ctpl-switch${state.isActive ? " on" : ""}`}
                            onClick={() => onChange((s) => ({ ...s, isActive: !s.isActive }))}
                        />
                    </div> */}
                </div>

                <div className="ctpl-drawer-footer">
                    <button className="ctpl-btn ctpl-btn-ghost" onClick={onClose}>Cancel</button>
                    <button className="ctpl-btn ctpl-btn-primary" onClick={handleSaveClick}>
                        {isEdit ? "Save changes" : "Save template"}
                    </button>
                </div>
            </div>
        </>
    );
}